import React, { useEffect, useState } from 'react';
import { getMySubscription, cancelSubscription } from '../Api/Subscriptionapi';
import '../Style/Dashboard.css';

const STORAGE_KEY = 'mlink_subscription';
// Tells Pricing.js the user came here to change plan, so it must not
// auto-redirect back to the Dashboard.
const CHANGE_FLAG = 'mlink_changing_plan';
const FLASH_KEY = 'mlink_flash'; // one-time message shown on the Pricing page

const TIER_LABELS = { basic: 'Basic', standard: 'Standard', pro: 'Pro' };
const TERM_LABELS = {
  monthly: 'Monthly',
  '3_months': '3 Months',
  '6_months': '6 Months',
  '1_year': '12 Months',
};
const LEGACY_PLAN_LABELS = {
  monthly: 'Basic · Monthly',
  '3_months': 'Standard · 3 Months',
  '6_months': 'Pro · 6 Months',
  '1_year': 'Pro · 12 Months',
};

function planLabel(planType) {
  for (const tier of Object.keys(TIER_LABELS)) {
    const prefix = `${tier}_`;
    if (planType.startsWith(prefix) && TERM_LABELS[planType.slice(prefix.length)]) {
      return `${TIER_LABELS[tier]} · ${TERM_LABELS[planType.slice(prefix.length)]}`;
    }
  }
  return LEGACY_PLAN_LABELS[planType] || planType;
}

function periodLabel(planType) {
  for (const term of Object.keys(TERM_LABELS)) {
    if (planType === term || planType.endsWith(`_${term}`)) return TERM_LABELS[term].toLowerCase();
  }
  return 'period';
}

const STATUS_LABELS = {
  trialing: 'Free trial',
  active: 'Active',
  past_due: 'Payment failed',
  cancelled: 'Cancelled',
  expired: 'Expired',
};

function readCachedSubscription() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function cacheSubscription(subscription) {
  if (subscription) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(subscription));
  } else {
    localStorage.removeItem(STORAGE_KEY);
  }
}

function markChangingPlan() {
  try {
    sessionStorage.setItem(CHANGE_FLAG, '1');
  } catch {
    /* ignore */
  }
}

function readUser() {
  try {
    const raw = localStorage.getItem('user');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function formatDateTime(value) {
  if (!value) return '—';
  return new Date(value).toLocaleString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function timeLeft(value, now) {
  if (!value) return '';
  const ms = new Date(value).getTime() - now;
  if (ms <= 0) return 'ending now';
  const mins = Math.floor(ms / 60000);
  const d = Math.floor(mins / 1440);
  const h = Math.floor((mins % 1440) / 60);
  const m = mins % 60;
  if (d > 0) return `${d}d ${h}h left`;
  if (h > 0) return `${h}h ${m}m left`;
  return `${m}m left`;
}

const money = (n) => `₹${Number(n).toLocaleString('en-IN')}`;

function initials(name) {
  if (!name) return '?';
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join('');
}

export default function Dashboard({ onChangePlan, onLogout, onOpenProfile }) {
  const [user] = useState(readUser);
  const [subscription, setSubscription] = useState(readCachedSubscription);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [cancelChoice, setCancelChoice] = useState('end'); // 'end' | 'now'
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const sub = await getMySubscription();
        setSubscription(sub);
        cacheSubscription(sub);
        if (!sub || !['trialing', 'active'].includes(sub.status)) {
          onChangePlan();
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function openCancelDialog() {
    setError('');
    setCancelChoice('end');
    setDialogOpen(true);
  }

  async function handleConfirmCancel() {
    setBusy(true);
    setError('');
    try {
      // The server only records the cancellation after Razorpay confirms it.
      const sub = await cancelSubscription(cancelChoice === 'end');
      setSubscription(sub);
      cacheSubscription(sub);
      setDialogOpen(false);
      if (sub.status === 'cancelled') {
        // Nothing is left to manage, so go back and choose a plan again.
        try {
          sessionStorage.setItem(
            FLASH_KEY,
            'Your subscription is cancelled and no further payments will be taken. Choose a plan to start again.'
          );
        } catch {
          /* ignore */
        }
        onChangePlan();
        return;
      }
      setNotice(
        `Your subscription is cancelled and no further payments will be taken. You keep access until ${formatDateTime(sub.access_until)}.`
      );
    } catch (err) {
      setError(err.message);
      setDialogOpen(false);
    } finally {
      setBusy(false);
    }
  }

  function handleChangePlan() {
    markChangingPlan();
    onChangePlan();
  }

  function handleOpenProfile() {
    if (onOpenProfile) onOpenProfile();
  }

  if (loading) {
    return (
      <section className="mlink-dashboard">
        <div className="mlink-dashboard__inner">
          <p className="mlink-dashboard__loading">Loading your account…</p>
        </div>
      </section>
    );
  }

  if (!subscription) {
    // onChangePlan() above already fired; render nothing while the parent
    // swaps the screen over to Pricing.
    return null;
  }

  const isTrialing = subscription.status === 'trialing';
  const isLive = subscription.status === 'trialing' || subscription.status === 'active';
  const cancelRequested = !!subscription.cancelled_at;
  const accessUntil =
    subscription.access_until ||
    (isTrialing ? subscription.trial_end_date : subscription.subscription_end_date);

  const statusText = isTrialing
    ? cancelRequested
      ? 'Free trial · cancelled'
      : `Free trial · ${timeLeft(subscription.trial_end_date, now)}`
    : subscription.status === 'active' && cancelRequested
    ? 'Active · cancelling'
    : STATUS_LABELS[subscription.status] || subscription.status;

  return (
    <section className="mlink-dashboard">
      <div className="mlink-dashboard__inner">
        <div className="mlink-dashboard__topbar">
          <span className="mlink-dashboard__brand">MLink</span>
          <button type="button" className="mlink-dashboard__logout" onClick={onLogout}>
            Log out
          </button>
        </div>

        <div className="mlink-dashboard__profile">
          <div className="mlink-dashboard__avatar">{initials(user?.name)}</div>
          <div className="mlink-dashboard__profile-info">
            <p className="mlink-dashboard__name">{user?.name || 'Your account'}</p>
            <p className="mlink-dashboard__email">{user?.email || '—'}</p>
          </div>
          <button type="button" className="mlink-dashboard__profile-btn" onClick={handleOpenProfile}>
            <svg
              className="mlink-dashboard__profile-icon"
              viewBox="0 0 24 24"
              width="16"
              height="16"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <circle cx="12" cy="8" r="4" />
              <path d="M4 20c0-4 4-6 8-6s8 2 8 6" />
            </svg>
            Profile
          </button>
        </div>

        {error && <p className="mlink-dashboard__error">{error}</p>}
        {notice && <p className="mlink-dashboard__notice">{notice}</p>}

        <div className={`mlink-dashboard__card mlink-dashboard__card--${subscription.status}`}>
          <div className="mlink-dashboard__card-header">
            <p className="mlink-dashboard__plan-name">
              {planLabel(subscription.plan_type)}
            </p>
            <span className={`mlink-dashboard__badge mlink-dashboard__badge--${subscription.status}`}>
              {STATUS_LABELS[subscription.status] || subscription.status}
            </span>
          </div>

          <p className="mlink-dashboard__price">
            &#8377;{subscription.price}
            {!isTrialing && subscription.status === 'active' && (
              <span> / {periodLabel(subscription.plan_type)}</span>
            )}
          </p>

          <dl className="mlink-dashboard__meta">
            <div>
              <dt>{isTrialing ? 'Trial ends' : 'Current period ends'}</dt>
              <dd>
                {isTrialing
                  ? formatDateTime(subscription.trial_end_date)
                  : formatDateTime(subscription.subscription_end_date)}
              </dd>
            </div>
            <div>
              <dt>Auto-renewal</dt>
              <dd>{subscription.auto_renewal && !cancelRequested ? 'On' : 'Off'}</dd>
            </div>
          </dl>

          <div className="mlink-dashboard__actions">
            <button type="button" className="mlink-dashboard__change" onClick={handleChangePlan}>
              Change plan
            </button>
          </div>
        </div>

        <div className="mlink-dashboard__manage">
          <p className="mlink-dashboard__manage-title">Manage subscription</p>

          <dl className="mlink-dashboard__manage-list">
            <div>
              <dt>Plan</dt>
              <dd>{planLabel(subscription.plan_type)}</dd>
            </div>
            <div>
              <dt>Status</dt>
              <dd>{statusText}</dd>
            </div>
            {subscription.with_trial && (
              <div>
                <dt>Trial expires</dt>
                <dd>{formatDateTime(subscription.trial_end_date)}</dd>
              </div>
            )}
            <div>
              <dt>Next debit</dt>
              <dd>
                {subscription.next_debit_at
                  ? `${money(subscription.renewal_amount)} on ${formatDateTime(subscription.next_debit_at)}`
                  : 'None scheduled'}
              </dd>
            </div>
            <div>
              <dt>Renewal amount</dt>
              <dd>{money(subscription.renewal_amount)}</dd>
            </div>
            <div>
              <dt>Cancellation</dt>
              <dd>
                {cancelRequested
                  ? `Cancelled · access until ${formatDateTime(accessUntil)}`
                  : subscription.status === 'cancelled'
                  ? 'Cancelled'
                  : 'Not cancelled'}
              </dd>
            </div>
          </dl>

          {isLive && !cancelRequested && (
            <button type="button" className="mlink-dashboard__cancel" onClick={openCancelDialog}>
              Cancel subscription
            </button>
          )}
        </div>
      </div>

      {dialogOpen && (
        <div className="mlink-dashboard__dialog-overlay" onClick={() => !busy && setDialogOpen(false)}>
          <div
            className="mlink-dashboard__dialog"
            role="dialog"
            aria-modal="true"
            aria-label="Cancel subscription"
            onClick={(e) => e.stopPropagation()}
          >
            <h3>Cancel your subscription?</h3>

            {isTrialing ? (
              <p>
                Your free trial ends right away and automatic payments stop, so you won&rsquo;t be charged{' '}
                {money(subscription.renewal_amount)}. You will need to choose a plan to continue. The &#8377;1 trial
                fee is not refundable.
              </p>
            ) : (
              <>
                <p>Choose when your access should end. No further payments will be taken either way.</p>
                <label className="mlink-dashboard__choice">
                  <input
                    type="radio"
                    name="cancel-choice"
                    checked={cancelChoice === 'end'}
                    onChange={() => setCancelChoice('end')}
                  />
                  <span>At the end of this billing period ({formatDateTime(subscription.subscription_end_date)})</span>
                </label>
                <label className="mlink-dashboard__choice">
                  <input
                    type="radio"
                    name="cancel-choice"
                    checked={cancelChoice === 'now'}
                    onChange={() => setCancelChoice('now')}
                  />
                  <span>Immediately. Access ends now.</span>
                </label>
              </>
            )}

            <div className="mlink-dashboard__dialog-actions">
              <button
                type="button"
                className="mlink-dashboard__dialog-keep"
                disabled={busy}
                onClick={() => setDialogOpen(false)}
              >
                Keep subscription
              </button>
              <button
                type="button"
                className="mlink-dashboard__dialog-confirm"
                disabled={busy}
                onClick={handleConfirmCancel}
              >
                {busy ? 'Cancelling…' : 'Yes, cancel'}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}